import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StorageManager from '../utils/StorageManager';
import LanguageSelector from './LanguageSelector';
import { AuthService } from '../Services/AuthService';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const token = AuthService.getToken();
      setIsLoggedIn(!!token);
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      StorageManager.safeRemoveItem('token');
      StorageManager.safeRemoveItem('username');
      StorageManager.safeRemoveItem('role');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('role');
      
      setIsLoggedIn(false);
      setUserRole(null);
      navigate('/');
    } catch (error) {
      console.error('Błąd podczas wylogowywania:', error);
      navigate('/');
    }
  };

  const navClasses = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
    isScrolled 
      ? 'bg-[#1A2F23] shadow-lg'
      : 'bg-gradient-to-b from-[#1A2F23] via-[#1A2F23]/70 to-transparent pb-4'
  }`;

  const linkClasses = `text-[#E6D5B8] hover:text-[#F0E6D8] transition duration-300 
    border-b-2 border-transparent hover:border-[#E6D5B8] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]`;

  return (
    <nav className={navClasses}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-20 relative">
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-[#E6D5B8] hover:text-[#F0E6D8] transition-colors z-20"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>

          <div className="absolute left-1/2 transform -translate-x-1/2 md:relative md:left-0 md:transform-none">
            <Link to="/" className="text-2xl font-bold text-[#E6D5B8] hover:text-[#F0E6D8] transition flex items-center">
              <img 
                src="/logo/logo.png" 
                alt="" 
                className="h-[100px] w-auto"
              />
            </Link>
          </div>

          <div className="md:hidden w-10"></div>

          <div className="hidden md:flex items-center space-x-4">
            <Link to="/events" className={linkClasses}>
              {t('nav.events')}
            </Link>

            <a 
              href="https://restauracjatortuga.goorder.pl/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`${linkClasses} font-semibold text-[#D4AF37]`}
            >
              Zamów online
            </a>

            {isLoggedIn && userRole === 'admin' && (
              <Link to="/admin" className={`${linkClasses} font-semibold`}>
                Panel Admin
              </Link>
            )}
            {isLoggedIn && userRole === 'scanner' && (
              <Link to="/scanner" className={`${linkClasses} font-semibold`}>
                Scanner
              </Link>
            )}

            {isLoggedIn && (
              <button
                onClick={handleLogout}
                className={`${linkClasses} px-4 py-2 rounded-lg 
                  bg-[#2A4434] hover:bg-[#1A2F23] 
                  border border-[#E6D5B8] hover:border-[#F0E6D8]`}
              >
                Wyloguj
              </button>
            )}

            <div className="border-l border-[#E6D5B8] pl-4">
              <LanguageSelector />
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden py-4 px-2 bg-[#1A2F23] border-t border-[#E6D5B8]/10">
            <div className="flex flex-col space-y-4">
              <Link
                to="/events"
                className="text-[#E6D5B8] hover:text-[#F0E6D8] py-2 text-center text-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.events')}
              </Link>

              <a
                href="https://restauracjatortuga.goorder.pl/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D4AF37] hover:text-[#F0E6D8] py-2 text-center text-lg font-semibold"
                onClick={() => setIsMenuOpen(false)}
              >
                Zamów online
              </a>

              {isLoggedIn && userRole === 'admin' && (
                <Link 
                  to="/admin" 
                  className="text-[#E6D5B8] hover:text-[#F0E6D8] py-2 text-center text-lg font-semibold"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Panel Admin
                </Link>
              )}
              
              {isLoggedIn && userRole === 'scanner' && (
                <Link 
                  to="/scanner" 
                  className="text-[#E6D5B8] hover:text-[#F0E6D8] py-2 text-center text-lg font-semibold"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Scanner
                </Link>
              )}

              {isLoggedIn && (
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="text-[#E6D5B8] hover:text-[#F0E6D8] py-2 text-center text-lg w-full"
                >
                  Wyloguj
                </button>
              )}

              <div className="pt-4 border-t border-[#E6D5B8]/10 flex justify-center">
                <LanguageSelector />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;